import React, { useEffect, useMemo, useState } from 'react'
import styles from './postItem.module.scss'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import axios from 'axios'
import { getAccessTokenFromStorage } from '../../utils'
import { formatDateToYYYYMMDD } from '../../utils/date'
import {
	CONTRACT_TYPE_LABEL,
	REGION_LABEL,
	compensationBlocks,
	contractPeriodLine,
	formatRelativeTimeKo,
	stripHtml,
} from '../../utils/jobPostCardDisplay'

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

const PostItem = ({ data, status = 'active', onClose }) => {
	const [itemData, setItemData] = useState(data)
	const [closing, setClosing] = useState(false)
	const navigate = useNavigate()

	useEffect(() => {
		setItemData(data)
	}, [data])

	const relativePosted = useMemo(
		() => formatRelativeTimeKo(itemData.createdAt),
		[itemData.createdAt],
	)

	const handleClosePost = async (e) => {
		e.stopPropagation()
		if (!window.confirm('이 공고를 마감할까요?')) return
		try {
			setClosing(true)
			const accessToken = getAccessTokenFromStorage()
			if (!accessToken) {
				alert('로그인이 필요합니다.')
				return
			}
			const response = await axios.patch(
				`${API_ENDPOINT}/job-post/${itemData.id}/close`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + accessToken,
					},
				},
			)
			if (response.status >= 200 && response.status < 300) {
				if (onClose) onClose(itemData.id)
				else navigate(0)
			} else {
				alert('공고 마감에 실패했습니다.')
			}
		} catch (error) {
			console.error('Error closing job post:', error)
			alert('공고 마감에 실패했습니다.')
		} finally {
			setClosing(false)
		}
	}

	const goDetail = (e) => {
		e.stopPropagation()
		navigate(`/posts/${itemData.id}`)
	}

	const goApplicants = (e) => {
		e.stopPropagation()
		navigate('/offers')
	}

	const isClosedPost = status === 'closed' || itemData.status === 'closed'
	const contractType = itemData.contractType
	const region = itemData.region
	const typeLabel = CONTRACT_TYPE_LABEL[contractType] ?? contractType ?? '계약'
	const regionLabel = region ? (REGION_LABEL[region] ?? region) : null
	const brief = stripHtml(itemData.content)
	const payBlocks = compensationBlocks(itemData)
	const periodLine = contractPeriodLine(itemData)
	const createdLabel = itemData.createdAt
		? formatDateToYYYYMMDD(new Date(itemData.createdAt))
		: ''
	const writerLabel = itemData.writerName ? `${itemData.writerName} 프로` : null
	const applicantCount = Number(itemData.applicantCount ?? 0)

	return (
		<div className={`${styles.card} ${isClosedPost ? styles.cardClosed : ''}`}>
			{createdLabel || relativePosted ? (
				<div className={styles.cardTopMeta}>
					{createdLabel ? <span className={styles.date}>등록 {createdLabel}</span> : null}
					{createdLabel && relativePosted ? (
						<span className={styles.topMetaSep} aria-hidden>
							·
						</span>
					) : null}
					{relativePosted ? (
						<span className={styles.relativeTime}>{relativePosted}</span>
					) : null}
				</div>
			) : null}
			<div className={styles.cardHeader}>
				<h2 className={`${styles.cardTitle} ${isClosedPost ? styles.cardTitleClosed : ''}`}>
					{itemData.subject ?? '(제목 없음)'}
				</h2>
				<button
					type="button"
					className={styles.detailChevronBtn}
					onClick={goDetail}
					aria-label="공고 상세 보기">
					<ChevronRightIcon className={styles.cardArrow} aria-hidden />
				</button>
			</div>
			<div className={styles.metaRow}>
				<span className={`${styles.badge} ${styles.badgeType}`}>{typeLabel}</span>
				{regionLabel ? (
					<span
						className={`${styles.badge} ${
							region === 'INTERNATIONAL'
								? styles.badgeRegionOverseas
								: styles.badgeRegionDomestic
						}`}>
						{regionLabel}
					</span>
				) : null}
				{isClosedPost ? (
					<span className={`${styles.badge} ${styles.badgeClosed}`}>마감</span>
				) : null}
				{writerLabel ? <span className={styles.writer}>{writerLabel}</span> : null}
			</div>
			{payBlocks.length > 0 ? (
				<div className={styles.compensation}>
					{payBlocks.map((b) => (
						<div key={b.key} className={styles.compItem}>
							<span className={styles.compLabel}>{b.label}</span>
							<span className={styles.compValue}>
								{b.value}
								<span className={styles.compUnit}>{b.unit}</span>
							</span>
						</div>
					))}
				</div>
			) : null}
			{brief ? <p className={styles.brief}>{brief}</p> : null}
			{periodLine ? <p className={styles.periodHint}>{periodLine}</p> : null}
			<div className={styles.actionBar}>
				<button type="button" className={styles.btnPrimary} onClick={goApplicants}>
					공고 지원자 ({applicantCount}명)
				</button>
				{isClosedPost ? (
					<button type="button" className={styles.btnSecondary} disabled>
						마감됨
					</button>
				) : (
					<button
						type="button"
						className={styles.btnSecondary}
						disabled={closing}
						onClick={handleClosePost}>
						{closing ? '처리 중…' : '마감'}
					</button>
				)}
			</div>
		</div>
	)
}

export default PostItem
